import { Section } from './types';

const GCS_DOCUMENTS_BASE = 'https://storage.googleapis.com/elevoniq-doc-extractor-files/documents';

/** Set to false to silence multivalue "Hinzufügen" debug logs. */
export const MULTIVALUE_DEBUG = true;

export function logMultivalueDebug(scope: string, payload: Record<string, unknown>) {
  if (!MULTIVALUE_DEBUG) return;
  console.log(`[multivalue:${scope}]`, JSON.stringify(payload, null, 2));
}

const datapoint = (
  schema_id: string,
  type: 'string' | 'enum' | 'number' | 'date',
  value = '',
): Section => ({
  category: 'datapoint',
  schema_id,
  value,
  type,
  rir_confidence: null,
});

const field = (
  schema_id: string,
  type: 'string' | 'enum' | 'number' | 'date',
  value = '',
): Section => ({
  category: 'field',
  schema_id,
  value,
  type,
  rir_confidence: null,
});

const section = (schema_id: string, children: Section[]): Section => ({
  category: 'section',
  schema_id,
  type: '',
  rir_confidence: null,
  children,
});

const multivalue = (schema_id: string, children: Section[] = []): Section => ({
  category: 'multivalue',
  schema_id,
  type: '',
  rir_confidence: null,
  children,
});

const tuple = (schema_id: string, children: Section[]): Section => ({
  category: 'tuple',
  schema_id,
  type: '',
  rir_confidence: null,
  children,
});

/** Customer contract (DocumentType 29192) — only multivalue tuple shapes for "Hinzufügen". */
const CONTRACT_29192_CONTENT: Section[] = [
  section('charged_service_elevator', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_charged_service_elevator_factory_id', 'string'),
        field('item_charged_service_elevator_name', 'string'),
        field('item_charged_service_elevator_netprice', 'number'),
        field('item_charged_service_elevator_comment', 'string'),
      ]),
    ]),
  ]),
  section('included_service_elevator', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_included_service_elevator_factory_id', 'string'),
        field('item_included_service_elevator_name', 'string'),
        field('item_included_service_elevator_comment', 'string'),
      ]),
    ]),
  ]),
  section('included_service_contract', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_included_service_contract_name', 'string'),
        field('item_included_service_contract_comment', 'string'),
      ]),
    ]),
  ]),
  section('included_spareparts', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_included_sparepart_name', 'string'),
        field('item_included_sparepart_comment', 'string'),
      ]),
    ]),
  ]),
  section('contract_options', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_contract_option_name', 'string'),
      ]),
    ]),
  ]),
  section('rates_surcharges', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_rates_surcharges_type', 'string'),
        field('item_rates_surcharges_start_day', 'string'),
        field('item_rates_surcharges_start_h', 'string'),
        field('item_rates_surcharges_end_day', 'string'),
        field('item_rates_surcharges_end_h', 'string'),
        field('item_rates_surcharges_netprice', 'number'),
        field('item_rates_surcharges_percent', 'number'),
        field('item_rates_surcharges_amount', 'number'),
        field('item_rates_surcharges_comment', 'string'),
      ]),
    ]),
  ]),
  section('fault_frequency_section', [
    multivalue('line_items', [
      tuple('line_item', [
        field('maximum_fault_frequency', 'number'),
        field('penalty_in_euro_frequency', 'number'),
        field('calculation_type_frequency', 'string'),
        field('comment_frequency', 'string'),
      ]),
    ]),
  ]),
  section('response_times_section', [
    multivalue('line_items', [
      tuple('line_item', [
        field('penalty_in_euro_reaction_time', 'number'),
        field('reaction_time_in_hours', 'number'),
        field('type', 'string'),
        field('calculation_type_reaction_time', 'string'),
        field('comment_reaction_time', 'string'),
      ]),
    ]),
  ]),
];

/** Customer invoice (DocumentType 29193) placeholder schema for array field templates. */
const INVOICE_29193_CONTENT: Section[] = [
  section('line_item_section', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_description', 'string'),
        field('item_factory_id', 'string'),
        field('item_quantity', 'number'),
        field('item_amount_base', 'number'),
        field('item_amount_total', 'number'),
        field('item_discount', 'number'),
        field('item_unit', 'string'),
        field('item_cost_group', 'string'),
      ]),
    ]),
  ]),
  section('price_adjustment_items', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_price_factory_no', 'string'),
        field('item_price_startdate', 'date'),
        field('item_price_adjustment_percent', 'number'),
        field('item_price_adjustment_amount', 'number'),
        field('item_price_comment', 'string'),
      ]),
    ]),
  ]),
  section('comment_section', [
    datapoint('comment', 'string', 'Placeholder comment'),
  ]),
  section('email_info_section', [
    datapoint('email_header_from', 'string', 'sender@example.com'),
    datapoint('email_header_subject', 'string', 'Invoice subject'),
    datapoint('email_tags', 'string', 'invoice'),
    datapoint('email_body', 'string', 'Email body placeholder'),
  ]),
];

/** Assessment report (DocumentType 22943) — only multivalue tuple shapes for "Hinzufügen". */
const ASSESSMENT_REPORT_22943_CONTENT: Section[] = [
  section('line_items_section', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_description', 'string'),
        field('item_deficiency_id', 'string'),
        field('item_result_pick', 'string'),
      ]),
    ]),
  ]),
  section('dangers_according_to_defect_712', [
    multivalue('line_items', [
      tuple('line_item', [
        field('danger_number', 'number'),
        field('danger_description', 'string'),
      ]),
    ]),
  ]),
  section('observation_item_section', [
    multivalue('line_items', [
      tuple('line_item', [
        field('observation_description', 'string'),
      ]),
    ]),
  ]),
];

/** Quotation (DocumentType 27388) — only multivalue tuple shapes for "Hinzufügen". */
const QUOTATION_27388_CONTENT: Section[] = [
  section('line_items_section', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_description', 'string'),
        field('item_amount_base', 'number'),
        field('item_quantity', 'number'),
        field('item_unit', 'string'),
        field('item_amount_total', 'number'),
        field('item_details', 'string'),
        field('item_factory_id', 'string'),
      ]),
    ]),
  ]),
];

/** Price adjustment (DocumentType 68303) — only multivalue tuple shapes for "Hinzufügen". */
const PRICE_ADJUSTMENT_68303_CONTENT: Section[] = [
  section('price_adjustment_items', [
    multivalue('line_items', [
      tuple('line_item', [
        field('item_price_factory_no', 'string'),
        field('Type', 'string'),
        field('item_change_in_Percentage', 'number'),
        field('item_new_price', 'number'),
        field('calculation_type', 'string'),
        field('item_price_comment', 'string'),
      ]),
    ]),
  ]),
];

/**
 * Other documents (Customer_Document) — only multivalue tuple shapes for "Hinzufügen".
 * Uses an internal template key since its DocumentType (29193) collides with invoices.
 */
const OTHER_DOCUMENT_TEMPLATE_KEY = 100001;
const OTHER_DOCUMENTS_CONTENT: Section[] = [
  section('general_info_section', [
    multivalue('factory_ids', [
      tuple('line_item', [
        field('item_factory_id', 'string'),
      ]),
    ]),
  ]),
];

export const DEFAULT_METADATA_CONTENT_BY_DOCUMENT_TYPE: Record<number, Section[]> = {
  22943: ASSESSMENT_REPORT_22943_CONTENT,
  27388: QUOTATION_27388_CONTENT,
  29192: CONTRACT_29192_CONTENT,
  29193: INVOICE_29193_CONTENT,
  68303: PRICE_ADJUSTMENT_68303_CONTENT,
  [OTHER_DOCUMENT_TEMPLATE_KEY]: OTHER_DOCUMENTS_CONTENT,
};

/** Fallback when jsonContent.DocumentType is missing on loaded documents. */
export const DOCUMENT_TYPE_BY_SALESFORCE_TYPE: Record<string, number> = {
  Assessment_Report_Prod: 22943,
  Assessment_Report_Stage: 22943,
  Customer_Contract_Prod: 29192,
  Customer_Contract_Stage: 29192,
  Customer_Document_Prod: OTHER_DOCUMENT_TEMPLATE_KEY,
  Customer_Document_Stage: OTHER_DOCUMENT_TEMPLATE_KEY,
  Customer_Invoice_Prod: 29193,
  Customer_Invoice_Stage: 29193,
  Customer_Quotation_Prod: 27388,
  Customer_Quotation_Stage: 27388,
  Price_Adjustment_Prod: 68303,
  Price_Adjustment_Stage: 68303,
};

/** Resolve placeholder template document type from Salesforce type (e.g. Customer_Invoice_Prod → 29193). */
export function resolveTemplateDocumentType(salesforceType?: string | null): number | undefined {
  if (salesforceType && DOCUMENT_TYPE_BY_SALESFORCE_TYPE[salesforceType]) {
    return DOCUMENT_TYPE_BY_SALESFORCE_TYPE[salesforceType];
  }
  return undefined;
}

export function resolveDocumentTypeNumber(
  jsonContent?: { DocumentType?: number | string } | null,
  salesforceType?: string | null,
): number | undefined {
  const rawType = jsonContent?.DocumentType;
  let parsedRaw: number | undefined;
  if (rawType !== undefined && rawType !== null && rawType !== '') {
    const parsed = Number(rawType);
    if (!Number.isNaN(parsed)) parsedRaw = parsed;
  }

  const salesforceMapped = salesforceType
    ? DOCUMENT_TYPE_BY_SALESFORCE_TYPE[salesforceType]
    : undefined;

  // Prefer raw DocumentType when a placeholder template exists for it.
  if (parsedRaw !== undefined && DEFAULT_METADATA_CONTENT_BY_DOCUMENT_TYPE[parsedRaw]) {
    return parsedRaw;
  }

  // e.g. DocumentType 26126 + Customer_Invoice_Prod → template 29193
  if (salesforceMapped !== undefined) {
    return salesforceMapped;
  }

  return parsedRaw;
}

type SchemaNode = Section;

function deepCloneSchema<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

/**
 * Collect multivalue tuple children keyed by dotted path (e.g. line_item_section.line_items).
 */
function extractMultivalueTupleChildrenByPath(
  content: SchemaNode[],
  basePath: string[] = [],
): Record<string, SchemaNode[]> {
  const defaults: Record<string, SchemaNode[]> = {};

  const walk = (items: SchemaNode[], path: string[]) => {
    items.forEach(item => {
      if (item.category === 'multivalue' && item.schema_id) {
        const fullPath = [...path, item.schema_id].filter(Boolean).join('.');
        if (item.children?.length) {
          defaults[fullPath] = deepCloneSchema(item.children);
        }
      }

      if (item.children?.length) {
        walk(item.children, item.schema_id ? [...path, item.schema_id] : path);
      }
    });
  };

  walk(content, basePath);
  return defaults;
}

function getDocumentTypeTemplate(documentType: number | string | undefined | null): SchemaNode[] | null {
  if (documentType === undefined || documentType === null || documentType === '') {
    return null;
  }

  return DEFAULT_METADATA_CONTENT_BY_DOCUMENT_TYPE[Number(documentType)] ?? null;
}

function findMultivalueChildrenForPath(
  defaults: Record<string, SchemaNode[]>,
  multivaluePath: string,
): SchemaNode[] | null {
  if (defaults[multivaluePath]?.length) {
    return defaults[multivaluePath];
  }

  const pathParts = multivaluePath.split('.').filter(Boolean);
  const sectionId = pathParts[0];
  if (!sectionId) return null;

  const sectionFallbackPath = Object.keys(defaults).find(path => path.startsWith(`${sectionId}.`));
  if (sectionFallbackPath && defaults[sectionFallbackPath]?.length) {
    return defaults[sectionFallbackPath];
  }

  const suffixFallbackPath = Object.keys(defaults).find(
    path => path === multivaluePath || path.endsWith(`.${multivaluePath}`),
  );
  if (suffixFallbackPath && defaults[suffixFallbackPath]?.length) {
    return defaults[suffixFallbackPath];
  }

  return null;
}

/** Tuple schema for a multivalue field path from the document-type default metadata. */
export function getMultivalueTupleSchema(
  multivaluePath: string,
  salesforceType?: string | null,
): SchemaNode | null {
  const resolvedType = resolveTemplateDocumentType(salesforceType);
  const template = getDocumentTypeTemplate(resolvedType);
  if (!template) return null;

  const children = findMultivalueChildrenForPath(
    extractMultivalueTupleChildrenByPath(template),
    multivaluePath,
  );
  if (!children?.length) return null;

  return children.find(child => child.category === 'tuple') ?? null;
}

/**
 * Resolve tuple schema for rendering/adding rows: prefer live extracted tuple, else salesforce-type template.
 */
export function resolveMultivalueTupleSchema(
  multivaluePath: string,
  salesforceType?: string | null,
  liveTuple?: SchemaNode | null,
): SchemaNode | null {
  const fromLive = liveTuple?.category === 'tuple' ? liveTuple : null;
  const fromTemplate = fromLive ? null : getMultivalueTupleSchema(multivaluePath, salesforceType);
  const result = fromLive ?? fromTemplate;

  if (!result) {
    logMultivalueDebug('resolveTuple:miss', {
      multivaluePath,
      salesforceType,
      resolvedType: resolveTemplateDocumentType(salesforceType),
      liveTupleCategory: liveTuple?.category,
      templatePaths: Object.keys(
        extractMultivalueTupleChildrenByPath(
          getDocumentTypeTemplate(resolveTemplateDocumentType(salesforceType)) ?? [],
        ),
      ),
    });
  }

  return result;
}

/** Build an empty form row for a multivalue field (structure from document type, empty values). */
export function buildEmptyFormRowFromTuple(tuple?: SchemaNode): Record<string, unknown> {
  if (!tuple?.children) return {};
  const row: Record<string, unknown> = {};

  const walk = (children: SchemaNode[], target: Record<string, unknown>) => {
    children.forEach(child => {
      if (!child.schema_id) return;

      switch (child.category) {
        case 'datapoint':
        case 'field':
          target[child.schema_id] = child.type === 'number' || child.type === 'date' ? null : '';
          break;
        case 'multivalue':
          target[child.schema_id] = [];
          break;
        case 'tuple':
        case 'section': {
          const nested: Record<string, unknown> = {};
          target[child.schema_id] = nested;
          walk(child.children || [], nested);
          break;
        }
        default:
          break;
      }
    });
  };

  walk(tuple.children, row);
  return row;
}

/** Empty row for "Hinzufügen" — shape from salesforce-type template, only used on button click. */
export function getEmptyMultivalueRow(
  multivaluePath: string,
  salesforceType?: string | null,
  liveTuple?: SchemaNode | null,
): Record<string, unknown> {
  const tuple = resolveMultivalueTupleSchema(multivaluePath, salesforceType, liveTuple);
  const row = buildEmptyFormRowFromTuple(tuple ?? undefined);

  logMultivalueDebug('emptyRow', {
    multivaluePath,
    salesforceType,
    hasTuple: !!tuple,
    rowKeys: Object.keys(row),
    row,
  });

  return row;
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function isMetadataEmpty(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== 'object') return true;
  const results = (metadata as { results?: unknown }).results;
  if (!Array.isArray(results) || results.length === 0) return true;
  const first = results[0];
  if (!first || typeof first !== 'object') return true;
  const content = (first as { content?: unknown }).content;
  return !Array.isArray(content) || content.length === 0;
}

function buildDocumentUrl(gstoreId: string): string {
  return `${GCS_DOCUMENTS_BASE}/${gstoreId}`;
}

export function ensureJsonContentMetadata(
  jsonContent: any,
  options: { filename?: string; salesforceType?: string | null } = {},
): any {
  if (!jsonContent || !isMetadataEmpty(jsonContent.Metadata)) {
    return jsonContent;
  }

  const documentType = resolveTemplateDocumentType(options.salesforceType);
  const template = documentType !== undefined
    ? DEFAULT_METADATA_CONTENT_BY_DOCUMENT_TYPE[documentType]
    : undefined;

  logMultivalueDebug('ensureMetadata', {
    rawDocumentType: jsonContent?.DocumentType,
    salesforceType: options.salesforceType,
    resolvedType: documentType,
    hasTemplate: !!template,
    metadataWasEmpty: true,
  });

  if (!template) return jsonContent;

  const gstoreId = String(jsonContent.GStoreId ?? '');
  const url = gstoreId ? buildDocumentUrl(gstoreId) : '';
  const filename = options.filename ?? '';

  return {
    ...jsonContent,
    Metadata: {
      ...(jsonContent.Metadata ?? {}),
      results: [
        {
          url,
          document: {
            url,
            file_name: filename,
            file: null,
          },
          arrived_at: jsonContent.arrived_at ?? new Date().toISOString(),
          exported_at: null,
          status: 'exported',
          content: deepClone(template),
        },
      ],
    },
  };
}
